import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../Common/Loading';
import alert_A_bg from '../../Common/Images/ImagesForAlert/alert_A_bg.png';
import alert_bg from '../../Common/Images/ImagesForAlert/alert_Q_bg_small.png';
import ok_n from '../../Common/Images/ImagesForAlert/ok_n.png';
import ok_o from '../../Common/Images/ImagesForAlert/ok_o.png';
import yes_n from '../../Common/Images/ImagesForAlert/yes_n.png';
import yes_o from '../../Common/Images/ImagesForAlert/yes_o.png';
import no_n from '../../Common/Images/ImagesForAlert/no_n.png';
import no_o from '../../Common/Images/ImagesForAlert/no_o.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';
interface Promotion {
    PromotionID: number;
    PackageCode: number;
    PackageDescription: string;
    Status: string;
    FromDate: string;
    ToDate: string;
    DiscountPercent: number;
    Price: number | null;
    Price_1: number | null;
    Price_3: number | null;
    Price_6: number | null;
    Price_12: number | null;
    PromotionCode: string;
    Remarks: string;
}

interface Package {
    PackageCode: number;
    PackageDescription: string;
}

interface ARDProps {
    schools: string[]; 
    initialSchool: string;
    onClose: () => void;
    onSaveSuccess: (school: string) => void;
    showWarning: (msg: string) => void;
    API_BASE_URL: string;
}

// --- Main Component ---
const EL_ManagePromotions: React.FC = () => {
    // State
    const navigate = useNavigate();
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [schools, setSchools] = useState<string[]>(['-- Select --']); 
    const [loading, setLoading] = useState<boolean>(false);
    const [filterSchool, setFilterSchool] = useState<string>(''); 
    const [selectedPromoID, setSelectedPromoID] = useState<number | null>(null);
    const [showARD, setShowARD] = useState(false);

    const [alertMsg, setAlertMsg] = useState('');
    const [confirmConfig, setConfirmConfig] = useState<{msg: string, onConfirm: () => void} | null>(null);

    const handleShowWarning = (msg: string) => setAlertMsg(msg);
    const handleShowConfirm = (msg: string, onConfirm: () => void) => setConfirmConfig({ msg, onConfirm });

    useEffect(() => {
        const originalBg = document.body.style.backgroundColor;
        document.body.style.backgroundColor = 'white';

        // 1. Get SchoolCode from URL
        const params = new URLSearchParams(window.location.search);
        const urlSchool = params.get('SchoolCode');

        // 2. Fetch School List (needed for ARD dropdown)
        fetchSchools(urlSchool);

        // 3. Set Filter & Fetch Data
        if (urlSchool) {
            setFilterSchool(urlSchool);
            fetchPromotions(urlSchool);
        } else {
            handleShowWarning("No School Code provided.");
        }

        return () => {
            document.body.style.backgroundColor = originalBg;
        };
    }, []);

    const fetchSchools = async (defaultSchool: string | null) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}api/manage-promotions/schools/`);
            if (response.ok) {
                const data = await response.json();
                setSchools(['-- Select --', ...data]); 
            }
        } catch (error) {
            console.error("Error fetching schools:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPromotions = async (schoolCodeToUse: string) => {
        setLoading(true);
        setSelectedPromoID(null);
        
        try {
            const response = await fetch(`${API_BASE_URL}api/manage-promotions/?school_code=${encodeURIComponent(schoolCodeToUse)}`);
            if (response.ok) {
                const data = await response.json();
                setPromotions(data);
            }
        } catch (error) {
            console.error("Error fetching promotions:", error);
            handleShowWarning("Failed to load promotions.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        if (!selectedPromoID) {
            handleShowWarning("Please select a Promotion to delete.");
            return;
        }
        handleShowConfirm('Do you really want to delete?', async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/manage-promotions/${selectedPromoID}/`, { method: 'DELETE' });
                if (response.ok) {
                    fetchPromotions(filterSchool);
                    handleShowWarning("Deleted Successfully.");
                } else {
                    handleShowWarning("Error deleting promotion");
                }
            } catch (e) {
                handleShowWarning("Network error");
            } finally {
                setLoading(false);
            }
        });
    };

    const isExpired = (dateStr: string) => {
        if (!dateStr) return false;
        const parts = dateStr.split('/'); 
        if (parts.length === 3) {
            const expiry = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            const now = new Date();
            now.setHours(0,0,0,0);
            return expiry < now;
        }
        return false;
    };

    const styles = {
        container: { fontFamily: 'Arial', fontSize: '10pt', width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' as const, overflow: 'hidden' },
        topBar: { textAlign: 'center' as const, padding: '10px' },
        btn: {
            backgroundColor: 'Olive',
            color: 'White',
            fontWeight: 'bold' as const,
            border: '2px solid black',
            padding: '2px 10px',
            marginRight: '15px',
            width: '140px',
            cursor: 'pointer',
            fontFamily: 'Arial',
            fontSize: '13px'
        },
        gridHeaderBar: {
            backgroundColor: '#C7DDFD',
            color: '#2859ac',
            fontWeight: 'bold' as const,
            padding: '8px',
            textAlign: 'center' as const,
            borderTop: '1px solid #7F9DB9',
            borderBottom: '1px solid #7F9DB9',
            fontSize: '10pt',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px'
        },
        gridContainer: { flex: 1, overflow: 'auto', backgroundColor: 'white' },
        table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '9pt' },
        th: {
            backgroundColor: '#C7DDFD',
            fontSize: '10pt',
            color: '#2859ac',
            borderRight: '1px solid #7F9DB9',
            borderBottom: '1px solid #7F9DB9',
            padding: '8px',
            position: 'sticky' as const,
            top: 0,
            zIndex: 1,
            textAlign: 'center' as const,
            fontWeight: 'normal' as const,
            whiteSpace: 'nowrap' as const
        },
        td: { borderRight: '1px solid #D3D3D3', padding: '8px', verticalAlign: 'middle' as const, whiteSpace: 'nowrap' as const, fontSize: '10pt' },
        schoolLabel: { fontSize: '10pt', fontWeight: 'bold' as const, color: '#2859ac' }
    };

    return (
        <div style={styles.container}>
            {!showARD && (
                <div style={styles.topBar} id="divButtons">
                    <button style={styles.btn} onClick={() => setShowARD(true)} id="cmdAdd">Add Promotion</button>
                    <button style={styles.btn} onClick={handleDelete} id="cmdDelete">Delete Promotion</button>
                    <button style={styles.btn} onClick={() => navigate('/SysAdmin')} id="cmdExit">Back to Menu</button>
                    <hr />
                </div>
            )}

            {!showARD && (
                <>
                    <div style={styles.gridHeaderBar}>
                        <span>Promotion offers for school code:</span>
                        <span style={styles.schoolLabel}>{filterSchool}</span>
                    </div>

                    <div style={styles.gridContainer} id="divList">
                        <div id="PROMO_divScrollableTable">
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>#</th>
                                        <th style={{...styles.th, textAlign: 'left'}}>PackageCode</th>
                                        <th style={{...styles.th, textAlign: 'left'}}>PackageDescription</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>FromDate</th>
                                        <th style={styles.th}>ToDate</th>
                                        <th style={styles.th}>DiscountPercent</th>
                                        <th style={{...styles.th, textAlign: 'right'}}>Price</th>
                                        <th style={{...styles.th, textAlign: 'right'}}>Price_1</th>
                                        <th style={{...styles.th, textAlign: 'right'}}>Price_3</th>
                                        <th style={{...styles.th, textAlign: 'right'}}>Price_6</th>
                                        <th style={{...styles.th, textAlign: 'right'}}>Price_12</th>
                                        <th style={{...styles.th, textAlign: 'left'}}>PromotionCode</th>
                                        <th style={{...styles.th, textAlign: 'left'}}>Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={14} align="center"><Loading /></td></tr>
                                    ) : promotions.length === 0 ? (
                                        <tr><td colSpan={14} align="center"></td></tr>
                                    ) : (
                                        promotions.map((row, index) => {
                                            const isSelected = selectedPromoID === row.PromotionID;
                                            const expired = isExpired(row.ToDate);
                                            const bgColor = isSelected ? 'orange' : 'white';
                                            const color = isSelected ? 'white' : (expired ? '#e2e2e2' : 'black');

                                            return (
                                                <tr 
                                                    key={row.PromotionID}
                                                    onClick={() => setSelectedPromoID(row.PromotionID)}
                                                    style={{ backgroundColor: bgColor, color: color, cursor: 'pointer' }}
                                                >
                                                    <td style={{...styles.td, textAlign: 'center'}}>{index + 1}</td>
                                                    <td style={styles.td}>{row.PackageCode}</td>
                                                    <td style={styles.td}>{row.PackageDescription}</td>
                                                    <td style={{...styles.td, textAlign: 'center'}}>{row.Status}</td>
                                                    <td style={{...styles.td, textAlign: 'center'}}>{row.FromDate}</td>
                                                    <td style={{...styles.td, textAlign: 'center'}}>{row.ToDate}</td>
                                                    <td style={{...styles.td, textAlign: 'center'}}>{row.DiscountPercent}</td>
                                                    <td style={{...styles.td, textAlign: 'right'}}>{row.Price}</td>
                                                    <td style={{...styles.td, textAlign: 'right'}}>{row.Price_1}</td>
                                                    <td style={{...styles.td, textAlign: 'right'}}>{row.Price_3}</td>
                                                    <td style={{...styles.td, textAlign: 'right'}}>{row.Price_6}</td>
                                                    <td style={{...styles.td, textAlign: 'right'}}>{row.Price_12}</td>
                                                    <td style={styles.td}>{row.PromotionCode}</td>
                                                    <td style={styles.td}>{row.Remarks}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {showARD && (
                <div style={{flex: 1, overflow: 'auto', backgroundColor: 'white'}}>
                    <ELPromotionsARD 
                        schools={schools}
                        initialSchool={filterSchool}
                        onClose={() => {
                            setShowARD(false);
                            fetchPromotions(filterSchool); 
                        }}
                        onSaveSuccess={(school) => {
                            setShowARD(false);
                            fetchPromotions(school); 
                        }}
                        showWarning={handleShowWarning}
                        API_BASE_URL={API_BASE_URL}
                    />
                </div>
            )}

            {alertMsg && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(204, 204, 204, 0.6)' }}>
                    <div style={{ width: '400px', height: '200px', backgroundImage: `url(${alert_A_bg})`, backgroundRepeat: 'no-repeat', backgroundSize: '100% 100%', position: 'relative', fontFamily: 'Tahoma, Arial', fontSize: '12px', color: 'black' }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>
                            Warning
                        </div>
                        <div style={{ position: 'absolute', top: '10px', left: '100px', right: '15px', bottom: '50px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', overflowY: 'auto' }}>
                            <span style={{ fontSize: '11pt' }}>{alertMsg}</span>
                        </div>
                        <div style={{ position: 'absolute', bottom: '10px', width: '100%', textAlign: 'center' }}>
                            <img src={ok_n} alt="OK" onClick={() => setAlertMsg('')} style={{ cursor: 'pointer' }} onMouseOver={(e) => e.currentTarget.src = ok_o} onMouseOut={(e) => e.currentTarget.src = ok_n} />
                        </div>
                    </div>
                </div>
            )}

            {confirmConfig && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(204, 204, 204, 0.6)' }}>
                    <div style={{ width: '306px', height: '210px', backgroundImage: `url(${alert_bg})`, backgroundRepeat: 'no-repeat', position: 'relative', fontFamily: 'Tahoma', fontSize: '13px' }}>
                        <div style={{ position: 'absolute', top: '5px', left: 0, width: '100%', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>
                            Confirmation
                        </div>
                        <div style={{ position: 'absolute', top: '35px', left: '70px', right: '10px', height: '110px', display: 'flex', alignItems: 'center', color: '#525252', fontSize: '11pt' }}>
                            {confirmConfig.msg}
                        </div>
                        <div style={{ position: 'absolute', bottom: '7px', width: '100%', textAlign: 'center' }}>
                            <img src={yes_n} alt="Yes" onClick={() => { confirmConfig.onConfirm(); setConfirmConfig(null); }} style={{ cursor: 'pointer', margin: '0 15px' }} onMouseOver={(e) => e.currentTarget.src = yes_o} onMouseOut={(e) => e.currentTarget.src = yes_n} />
                            <img src={no_n} alt="No" onClick={() => setConfirmConfig(null)} style={{ cursor: 'pointer', margin: '0 15px' }} onMouseOver={(e) => e.currentTarget.src = no_o} onMouseOut={(e) => e.currentTarget.src = no_n} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ELPromotionsARD: React.FC<ARDProps> = ({ schools, initialSchool, onClose, onSaveSuccess, showWarning, API_BASE_URL }) => {
    const [selectedSchool, setSelectedSchool] = useState(initialSchool);
    const [packages, setPackages] = useState<Package[]>([]);
    const [packageID, setPackageID] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [discount, setDiscount] = useState('');
    const [price, setPrice] = useState('');
    const [price1, setPrice1] = useState('');
    const [price3, setPrice3] = useState('');
    const [price6, setPrice6] = useState('');
    const [price12, setPrice12] = useState('');
    const [promoCode, setPromoCode] = useState('');
    const [remarks, setRemarks] = useState('');
    const [status, setStatus] = useState('A');
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        // Load packages immediately using the selected school
        if (selectedSchool && selectedSchool !== '-- Select --') {
            const fetchPkgs = async () => {
                try {
                    const res = await fetch(`${API_BASE_URL}api/manage-promotions/packages/?school_code=${selectedSchool}`);
                    if (res.ok) setPackages(await res.json());
                } catch(e) { console.error(e); }
            };
            fetchPkgs();
        } else {
            setPackages([]);
        }
    }, [selectedSchool, API_BASE_URL]);

    useEffect(() => {
        if (packages.length > 0 && !packageID) {
            setPackageID(packages[0].PackageCode.toString());
        }
    }, [packages]);

    const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSchool(e.target.value);
        setPackageID(''); // Reset package selection on school change
    };

    const validateDate = (d: string) => /^\d{2}\/\d{2}\/\d{4}$/.test(d);

    const handleSave = async () => {
        if (!selectedSchool || selectedSchool === '-- Select --') return showWarning("Select a School.");
        if (!packageID) return showWarning("Select a Package.");
        
        if (!fromDate) return showWarning("Please Enter From Date");
        if (!validateDate(fromDate)) return showWarning("Please Enter a valid From Date in dd/MM/yyyy format");
        
        if (!toDate) return showWarning("Please Enter To Date");
        if (!validateDate(toDate)) return showWarning("Please Enter a valid To Date in dd/MM/yyyy format");
        
        if (!discount) return showWarning("Please Enter Discount Percent");
        if (isNaN(Number(discount))) return showWarning("Enter Valid number for Discount");

        const prices = [price, price1, price3, price6, price12];
        if (prices.every(p => !p.trim())) return showWarning("Please enter at least one 'Promotion Price'.");
        if (prices.some(p => p && isNaN(Number(p)))) return showWarning("Enter a valid numeric price.");

        if (!remarks) return showWarning("Please Enter details of promotion in this remarks field.");

        setLoading(true);

        const payload = {
            SchoolCode: selectedSchool,
            PackageCode: packageID,
            FromDate: fromDate,
            ToDate: toDate,
            DiscountPercent: discount,
            Price: price,
            Price_1: price1,
            Price_3: price3,
            Price_6: price6,
            Price_12: price12,
            PromotionCode: promoCode,
            Remarks: remarks,
            Status: status
        };

        try {
            const res = await fetch(`${API_BASE_URL}api/manage-promotions/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                onSaveSuccess(selectedSchool);
                showWarning("Added Successfully.");
            } else {
                showWarning("Failed to save. Check inputs.");
            }
        } catch(e) { showWarning("Network Error"); }
        finally { setLoading(false); }
    };

    const styles = {
        container: { width: '95%', backgroundColor: '#F4FFF5', border: '1px solid gray', padding: '15px 15px', margin: '20px auto', textAlign: 'left' as const },
        table: { width: '70%', borderCollapse: 'collapse' as const, fontSize: '10pt' },
        tdLabel: { width: '30%', padding: '5px' },
        tdInput: { width: '70%', padding: '5px' },
        input: { width: '400px', border: '1px solid #003300', padding: '2px' },
        select: { width: '400px', border: '1px solid #003300', padding: '2px' },
        btn: { backgroundColor: 'Olive', color: 'White', fontWeight: 'bold' as const, width: '120px', cursor: 'pointer', padding: '2px', marginRight: '15px', border: '2px solid black' }
    };

    return (
        <div style={styles.container} id="divAdd">
            {loading && <Loading />}
            <br />
            <table style={styles.table} id="tableKeys">
                <tbody>
                    <tr>
                        <td style={styles.tdLabel}>SchoolCode</td>
                        <td style={styles.tdInput}>
                            {/* Re-enabled Select Dropdown for ARD mode */}
                            <select style={styles.select} value={selectedSchool} onChange={handleSchoolChange} id="ddlSchoolCode">
                                {schools.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </td>
                    </tr>
                    <tr>
                        <td style={styles.tdLabel}>Package</td>
                        <td style={styles.tdInput}>
                            <select style={styles.select} value={packageID} onChange={e => setPackageID(e.target.value)} id="cmbPackageID">
                                <option value="">-- Select --</option>
                                {packages.map(p => (
                                    <option key={p.PackageCode} value={p.PackageCode}>
                                        {p.PackageDescription}
                                    </option>
                                ))}
                            </select>
                        </td>
                    </tr>
                </tbody>
            </table>
            <br/><hr/><br/>

            <table style={styles.table} id="tableDetails">
                <tbody>
                    <tr><td style={styles.tdLabel}>From Date (dd/MM/yyyy)</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={fromDate} onChange={e => setFromDate(e.target.value)} id="txtFromDate"/></td></tr>
                    <tr><td style={styles.tdLabel}>To Date (dd/MM/yyyy)</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={toDate} onChange={e => setToDate(e.target.value)} id="txtToDate"/></td></tr>
                    <tr><td style={styles.tdLabel}>Discount Percent</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={discount} onChange={e => setDiscount(e.target.value)} id="txtDiscountPercent"/></td></tr>
                    <tr><td style={styles.tdLabel}>Price</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={price} onChange={e => setPrice(e.target.value)} id="txtPrice"/></td></tr>
                    <tr><td style={styles.tdLabel}>Price / month</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={price1} onChange={e => setPrice1(e.target.value)} id="txtPrice_1"/></td></tr>
                    <tr><td style={styles.tdLabel}>Price / 3 months</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={price3} onChange={e => setPrice3(e.target.value)} id="txtPrice_3"/></td></tr>
                    <tr><td style={styles.tdLabel}>Price / 6 months</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={price6} onChange={e => setPrice6(e.target.value)} id="txtPrice_6"/></td></tr>
                    <tr><td style={styles.tdLabel}>Price / year</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={price12} onChange={e => setPrice12(e.target.value)} id="txtPrice_12"/></td></tr>
                    <tr><td style={styles.tdLabel}>Promotions Code</td><td style={styles.tdInput}><input style={styles.input} maxLength={10} value={promoCode} onChange={e => setPromoCode(e.target.value)} id="txtPromotionCode"/></td></tr>
                    <tr>
                        <td style={styles.tdLabel}>Remarks</td>
                        <td style={styles.tdInput}><textarea style={{...styles.input, height:'60px'}} maxLength={50} value={remarks} onChange={e => setRemarks(e.target.value)} id="txtRemarks"/></td>
                    </tr>
                    <tr>
                        <td style={styles.tdLabel}>Status</td>
                        <td style={styles.tdInput}>
                            <select style={{width:'90px', border: '1px solid #003300'}} value={status} onChange={e => setStatus(e.target.value)} id="cmbStatus">
                                <option value="A">Active</option>
                                <option value="N">Inactive</option>
                            </select>
                        </td>
                    </tr>
                    <tr><td colSpan={2}>&nbsp;</td></tr>
                    <tr>
                        <td></td>
                        <td>
                            <button style={styles.btn} onClick={handleSave} id="cmdSave">Save</button>
                            <button style={styles.btn} onClick={onClose} id="cmdCancel">Cancel</button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <br/><br/>
        </div>
    );
};

export default EL_ManagePromotions;